/**
 * Browser push notifications on this device (2026-10-14). The browser's permission popup only ever
 * opens from a click on "Turn on" (never on page load). Standard Web Push with Arevina's own keys:
 * the public key comes from the pushApi callable; the subscription is stored server-side only.
 *
 * iPhone and iPad: push works only for Arevina added to the Home Screen (iOS/iPadOS 16.4 and later,
 * webkit.org, claims register section 23). In a Safari tab there, the page shows the "Add Arevina to
 * your Home Screen" steps instead of a button that cannot work.
 */
const SW_URL = '/push-sw.js';
export const PUSH_SNOOZE_KEY = 'rr-push-prompt-snooze-until';
export const PUSH_SNOOZE_DAYS = 7;

export type PushSupport =
  | { kind: 'supported' }
  /** iPhone/iPad in a browser tab: add to the Home Screen first. */
  | { kind: 'ios_needs_home_screen' }
  | { kind: 'unsupported' };

export function isIosDevice(nav: { userAgent: string; platform?: string; maxTouchPoints?: number } = navigator): boolean {
  const ua = nav.userAgent || '';
  // iPadOS reports itself as a Mac; a touch screen tells them apart.
  return /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && (nav.maxTouchPoints || 0) > 1);
}

export function isStandaloneApp(): boolean {
  try {
    return window.matchMedia?.('(display-mode: standalone)').matches === true || (navigator as unknown as { standalone?: boolean }).standalone === true;
  } catch {
    return false;
  }
}

export function pushSupport(): PushSupport {
  if (typeof window === 'undefined') return { kind: 'unsupported' };
  const has = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  if (isIosDevice() && !isStandaloneApp()) return { kind: 'ios_needs_home_screen' };
  return has ? { kind: 'supported' } : { kind: 'unsupported' };
}

export function permissionState(): NotificationPermission | 'unsupported' {
  try {
    return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
  } catch {
    return 'unsupported';
  }
}

export function snoozedUntil(): number {
  try {
    return Number(localStorage.getItem(PUSH_SNOOZE_KEY)) || 0;
  } catch {
    return 0;
  }
}
export function snoozePushPrompt(nowMs = Date.now()): void {
  try {
    localStorage.setItem(PUSH_SNOOZE_KEY, String(nowMs + PUSH_SNOOZE_DAYS * 24 * 3600 * 1000));
  } catch {
    /* private window: the prompt simply shows again next time */
  }
}
export function isPushPromptSnoozed(nowMs = Date.now()): boolean {
  return snoozedUntil() > nowMs;
}

async function pushApi<T>(action: string, data: Record<string, unknown> = {}): Promise<T> {
  const { getFunctions, httpsCallable } = await import('firebase/functions');
  const { app } = await import('../firebase');
  if (!app) throw new Error('Firebase app not initialized');
  const fn = httpsCallable<Record<string, unknown>, T>(getFunctions(app), 'pushApi', { timeout: 30000 });
  return (await fn({ ...data, action })).data;
}

function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function registration(): Promise<ServiceWorkerRegistration> {
  const existing = await navigator.serviceWorker.getRegistration('/');
  if (existing && existing.active?.scriptURL.endsWith(SW_URL)) return existing;
  return navigator.serviceWorker.register(SW_URL, { scope: '/' });
}

/** Is push on for this device (permission granted, subscribed here, and known to the server)? */
export async function isPushOnHere(): Promise<boolean> {
  if (pushSupport().kind !== 'supported' || permissionState() !== 'granted') return false;
  const reg = await navigator.serviceWorker.getRegistration('/');
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return false;
  try {
    return (await pushApi<{ on: boolean }>('status', { endpoint: sub.endpoint })).on;
  } catch {
    return true;
  }
}

export type TurnOnResult = 'on' | 'denied' | 'dismissed' | 'unsupported' | 'failed';

/** "Turn on": must be called from the click itself (the permission popup needs a user gesture). */
export async function turnOnPush(): Promise<TurnOnResult> {
  if (pushSupport().kind !== 'supported') return 'unsupported';
  let perm: NotificationPermission;
  try {
    perm = await Notification.requestPermission();
  } catch {
    return 'failed';
  }
  if (perm === 'denied') return 'denied';
  if (perm !== 'granted') return 'dismissed';
  try {
    const reg = await registration();
    await navigator.serviceWorker.ready;
    const { publicKey } = await pushApi<{ publicKey: string | null }>('config');
    if (!publicKey) return 'failed';
    let sub = await reg.pushManager.getSubscription();
    if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(publicKey) as BufferSource });
    await pushApi('subscribe', { subscription: sub.toJSON(), userAgent: navigator.userAgent.slice(0, 200) });
    return 'on';
  } catch (err) {
    console.warn('[push] could not turn on', err);
    return 'failed';
  }
}

/** "Off" for this device: the browser forgets the subscription and the server stops sending to it. */
export async function turnOffPushHere(): Promise<void> {
  const reg = await navigator.serviceWorker?.getRegistration('/');
  const sub = await reg?.pushManager.getSubscription();
  if (sub) {
    await pushApi('unsubscribe', { endpoint: sub.endpoint }).catch(() => undefined);
    await sub.unsubscribe().catch(() => undefined);
  }
}

/** The red number: "(3) Arevina" in the tab, and the installed app's icon where the browser supports it. */
export function applyUnreadBadge(count: number, baseTitle: string): string {
  const n = Math.max(0, Math.floor(count || 0));
  const clean = baseTitle.replace(/^\(\d+\+?\)\s+/, '');
  const title = n > 0 ? `(${n > 99 ? '99+' : n}) ${clean}` : clean;
  try {
    const nav = navigator as Navigator & { setAppBadge?: (n?: number) => Promise<void>; clearAppBadge?: () => Promise<void> };
    if (n > 0) void nav.setAppBadge?.(n).catch(() => undefined);
    else void nav.clearAppBadge?.().catch(() => undefined);
  } catch {
    /* no badge support */
  }
  return title;
}
